import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import {
  appendTransactionStatuses,
  setTransactionStatuses,
  updateTransactionStatus,
} from 'dashboard/redux/actions/transaction-statuses';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiListResponse, ApiModel, PaginationParams } from 'lib/interfaces/api';

const DEFAULT_PAGE_SIZE = 100;

const loadTransactionStatusesByPage = async (
  page = 1,
): Promise<ApiListResponse<TransactionStatus, PaginationParams>> => {
  return SessionClientDashboard.listResource<TransactionStatus, PaginationParams>(
    ApiEndpoint.TransactionStatus,
    {
      page: { size: DEFAULT_PAGE_SIZE, number: page },
    },
  );
};

export const loadTransactionStatuses =
  (): DashboardThunkAction<Promise<void>> => async (dispatch) => {
    const firstPageResponse = await loadTransactionStatusesByPage(1);

    if (!firstPageResponse.meta) {
      throw new Error('Transaction statuses response missing pagination metadata');
    }

    const { 'total-pages': totalPages } = firstPageResponse.meta;
    dispatch(setTransactionStatuses(firstPageResponse.data));

    if (totalPages > 1) {
      const pages = Array.from(Array(totalPages).keys(), (x) => x + 1);
      pages.shift();

      const responses = await Promise.all(
        pages.map((page) => {
          return loadTransactionStatusesByPage(page);
        }),
      );

      dispatch(appendTransactionStatuses(responses.map(({ data }) => data).flat()));
    }
  };

export const loadTransactionStatusesByTransactionType =
  (transactionTypeId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const params = {
      filter: {
        'transaction-type-id': transactionTypeId,
      },
    };

    const transactionStatusesResponse =
      await SessionClientDashboard.listResource<TransactionStatus>(
        ApiEndpoint.TransactionStatus,
        params,
      );

    const transactionStatuses = transactionStatusesResponse.data;

    dispatch(setTransactionStatuses(transactionStatuses));
  };

export const addTransactionStatus =
  (name: string, transactionTypeId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: transactionStatus } = await SessionClientDashboard.postResourceCreate<
      TransactionStatus,
      {
        type: ApiModel.TransactionStatus;
        attributes: { name: string; 'transaction-type-id': string };
      }
    >({
      type: ApiModel.TransactionStatus,
      attributes: { name: name, 'transaction-type-id': transactionTypeId },
    });

    dispatch(appendTransactionStatuses([transactionStatus]));
  };

export const renameTransactionStatus =
  (name: string, transactionStatusId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const transactionStatusResponse = await SessionClientDashboard.updateResource<
      TransactionStatus,
      {
        type: ApiModel.TransactionStatus;
        id: string;
        attributes: { name: string };
      }
    >({
      type: ApiModel.TransactionStatus,
      id: transactionStatusId,
      attributes: { name },
    });

    dispatch(updateTransactionStatus(transactionStatusResponse.data));
  };

export const deactivateTransactionStatus =
  (transactionStatusId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const transactionStatusResponse =
      await SessionClientDashboard.postResourceAction<TransactionStatus>(
        {
          type: ApiModel.TransactionStatus,
          id: transactionStatusId,
        },
        'deactivate',
      );

    dispatch(updateTransactionStatus(transactionStatusResponse.data));
  };

export const activateTransactionStatus =
  (transactionStatusId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const transactionStatusResponse =
      await SessionClientDashboard.postResourceAction<TransactionStatus>(
        {
          type: ApiModel.TransactionStatus,
          id: transactionStatusId,
        },
        'activate',
      );

    dispatch(updateTransactionStatus(transactionStatusResponse.data));
  };
